import './assets/main.css'

import { patchWebkitMessageHandlersForWeb } from '@/utils/iosNativeBridge'

patchWebkitMessageHandlersForWeb()

import { createApp } from 'vue'
import { createPinia } from 'pinia'
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate'
import Notifications from 'notiwind'
import { useAuthGlobalStore } from '@/stores/authGlobalStore'
import { useRestoGLobalStore } from '@/stores/restoGlobalStore'
import App from './App.vue'
import router from './router'
import * as Sentry from '@sentry/vue'
import '@vuepic/vue-datepicker/dist/main.css'
import 'timepicker-ui/main.css'
import { useDeepLinking } from '@/composables/useDeepLinking'
import { useAppUpgradeStore } from '@/stores/appUpgradeStore'

const app = createApp(App)

window.addEventListener('vite:preloadError', (event) => {
  // Recover from stale hashed assets after deploy by forcing a clean reload.
  event.preventDefault()
  if (!sessionStorage.getItem('vite_preload_retry_done')) {
    sessionStorage.setItem('vite_preload_retry_done', '1')
    window.location.reload()
    return
  }
  sessionStorage.removeItem('vite_preload_retry_done')
})

const SENTRY_IGNORED_CONSOLE_MESSAGES = [
  'Please login to access this resource',
  'The requested resource does not exist or has been deleted',
  'connection problems..',
  'connection canceled..',
  'Push registration success',
  'Device token already registered',
  'Push registration temporarily unavailable',
  'Push registration unavailable',
  'Push notification registration failed',
  'Device token sent successfully',
  'token listener registered',
  'security_scan_validation_probe',
  'Failed to get initial setup'
]

// Recoverable FCM / GMS failures (emulator, no Play Services, network) — handled in-app
const SENTRY_IGNORED_PUSH_REGISTRATION_PATTERNS = [
  'service_not_available',
  'google play services',
  'missing_instanceid',
  'api_unavailable'
]

// Facebook / Instagram in-app browser, extensions, third-party scripts (not app bugs)
const SENTRY_IGNORED_MESSAGE_PREFIXES = [
  'FBNav',
  'hxp-chat',
  'ObjectMultiplex',
  'antifingerprint',
  'wptagent',
  'PageTranslationRequiredListener',
  'Browsing Topics API removed'
]

function getSentryEventMessage(event: Sentry.ErrorEvent): string {
  return (
    event.message ||
    (typeof event.logentry?.message === 'string' ? event.logentry.message : '') ||
    event.exception?.values?.[0]?.value ||
    ''
  )
}

function isCrossOriginStylesheetSecurityError(message: string): boolean {
  const lower = message.toLowerCase()
  return (
    lower.includes('cssstylesheet') &&
    (lower.includes('cssrules') || lower.includes('cross-origin stylesheet'))
  )
}

function isRecoverablePushRegistrationMessage(message: string): boolean {
  const lower = message.toLowerCase()
  return (
    lower.includes('push registration error') &&
    SENTRY_IGNORED_PUSH_REGISTRATION_PATTERNS.some((pattern) => lower.includes(pattern))
  )
}

function isWebkitMessageHandlersNoise(message: string): boolean {
  const lower = message.toLowerCase()
  return (
    lower.includes('webkit.messagehandlers') ||
    (lower.includes('messagehandlers') && lower.includes('webkit'))
  )
}

function shouldIgnoreSentryEvent(message: string): boolean {
  return (
    SENTRY_IGNORED_CONSOLE_MESSAGES.some((snippet) => message.includes(snippet)) ||
    SENTRY_IGNORED_MESSAGE_PREFIXES.some((prefix) => message.includes(prefix)) ||
    message.includes('[hmr]') ||
    isCrossOriginStylesheetSecurityError(message) ||
    isRecoverablePushRegistrationMessage(message) ||
    isWebkitMessageHandlersNoise(message)
  )
}

function isDevSentryEvent(event: Sentry.ErrorEvent): boolean {
  if (import.meta.env.DEV) {
    return true
  }
  const url = event.request?.url ?? ''
  return url.includes('localhost') || url.includes('127.0.0.1')
}

Sentry.init({
  app,
  dsn: 'https://56386e93cee69a75a7fed8e00bbd861c@o4507588414799872.ingest.de.sentry.io/4507588416962640',
  environment: import.meta.env.MODE || 'development',
  ignoreErrors: [
    /Not allowed to access cross-origin stylesheet/i,
    /CSSStyleSheet\.cssRules.*cross-origin/i,
    /Blocked a frame with origin .* from accessing a cross-origin frame/i
  ],
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
      // GTM / ads iframes — replay cannot access cross-origin frames (TICKET-011)
      block: ['iframe']
    }),
    Sentry.captureConsoleIntegration({
      levels: ['error', 'warn']
    })
  ],
  beforeSend(event) {
    if (isDevSentryEvent(event)) {
      return null
    }
    if (shouldIgnoreSentryEvent(getSentryEventMessage(event))) {
      return null
    }
    return event
  },
  // Performance Monitoring
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  // disable distributed tracing for localhost and enable it for your API
  // tracePropagationTargets: ['localhost', /^https:\/\/yourserver\.io\/api/],
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0 // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
})

const pinia = createPinia()
pinia.use(piniaPluginPersistedstate)
app.use(Notifications)
app.use(pinia)

const authGlobalStore = useAuthGlobalStore()
const restoGlobalStore = useRestoGLobalStore()

router.beforeEach(async (to) => {
  // Normalize accidental double slashes (e.g. /rstm//epos/... from email links)
  if (to.path.includes('//')) {
    return {
      path: to.path.replace(/\/{2,}/g, '/'),
      query: to.query,
      hash: to.hash,
      replace: true
    }
  }

  if (import.meta.env.VITE_MOBILE_APP) {
    const appUpgradeStore = useAppUpgradeStore()
    if (appUpgradeStore.isBlocking) {
      return false
    }
  }

  // set resto id globally
  if (!restoGlobalStore.RESTO_ID) {
    await restoGlobalStore.handleCheckRestoId()
    // if resto id is not set, redirect to error page
    if (!restoGlobalStore.RESTO_ID && to.name !== 'error' && to.name !== 'epos_order_payment_link') {
      return {
        name: 'error',
        // save the location we were at to come back later
        query: { redirect: to.fullPath }
      }
    }
  }

  // instead of having to check every route record with
  // to.matched.some(record => record.meta.requiresAuth)

  if (to.meta.requiresAuth) {
    if (!authGlobalStore.isLoggedIn) {
      // this route requires auth, check if logged in
      // if not, redirect to login page.
      return {
        name: 'login',
        // save the location we were at to come back later
        query: { redirect: to.fullPath }
      }
    }
    try {
      const user_data = await authGlobalStore.getAuthUser()
      authGlobalStore.setAuthUser(user_data)
    } catch (error) {
      authGlobalStore.logout().then(() => {
        router.push({ name: 'login', query: { redirect: to.fullPath } })
      })
    }
  }
  const restoName = (restoGlobalStore.restoInfo as { name?: string }).name
  document.title = (to.meta.title as string | undefined) || restoName || ''
})

app.provide('IS_APP_DYNAMIC', import.meta.env.VITE_APP_DYNAMIC)
app.provide('IS_MOBILE_APP', import.meta.env.VITE_MOBILE_APP)
app.use(router)

router.isReady().then(() => {
  const siteSettings = restoGlobalStore.SITE_SETTINGS as { google_analytics_key?: string }
  const googleAnalyticsKey = siteSettings?.google_analytics_key

  if (googleAnalyticsKey) {
    // Inject GTM script dynamically
    ;(function (w: Window, d: Document, s: string, l: string, i: string) {
      const layer = w as unknown as Record<string, unknown[]>
      layer[l] = layer[l] || []
      layer[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' })
      const f = d.getElementsByTagName(s)[0]
      const j = d.createElement(s) as HTMLScriptElement
      const dl = l !== 'dataLayer' ? '&l=' + l : ''
      j.async = true
      j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl
      f?.parentNode?.insertBefore(j, f)
    })(window, document, 'script', 'dataLayer', googleAnalyticsKey)

    const noscript = document.createElement('noscript')
    noscript.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${googleAnalyticsKey}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`
    document.body.appendChild(noscript)
  }
})

router.afterEach((to) => {
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({
    event: 'pageview',
    page_path: to.fullPath,
    page_name: to.name
  })
})
// app.mount('#app')
// wait until router is ready before mounting to ensure hydration match
// otherwise test will fail to use store
router.isReady().then(async () => {
  if (import.meta.env.VITE_MOBILE_APP) {
    const { initDeepLinking } = useDeepLinking()
    await initDeepLinking()
  }
  app.mount('#app')
})
